/**
 * CMP-11 — chemical mechanical planarisation.
 *
 * The tool that makes multilevel wiring possible. Every deposition and etch
 * step leaves topography, it accumulates over a dozen metal layers, and a
 * scanner cannot print a fine feature on a surface with hundreds of nanometres
 * of relief - LITH-03 in this same codebase loses a fifth of its image contrast
 * to thirty nanometres of defocus. Flattening between layers is not tidying up;
 * it is the enabling step.
 *
 * It is also how copper is patterned at all. Copper has no practical plasma
 * etch - its halides are not volatile at any sensible temperature - so instead
 * of etching metal you etch trenches in the dielectric, overfill with plated
 * copper, and polish everything above the trench away. There, CMP is not a
 * cleanup step. It IS the patterning step, and that is what damascene means.
 *
 * FOUR THINGS THIS MODULE HAS TO GET RIGHT
 *
 *   PRESTON        removal goes as pressure times relative velocity. The
 *                  Deal-Grove of this process: two variables, one coefficient,
 *                  honest over the range a tool actually runs in.
 *
 *   THE VELOCITY   turn the platen and the carrier at the SAME speed and the
 *   RESULT         relative velocity is identical everywhere on the wafer -
 *                  exactly, not approximately. It falls out of the kinematics
 *                  in two lines and it is why every rotary polisher on the
 *                  market is built that way.
 *
 *   PATTERN        removal depends on how much raised area is carrying the
 *   DENSITY        load. An isolated feature takes the whole force; a dense
 *                  array shares it. That single fact produces both classic
 *                  CMP defects and neither of them is the polisher's fault.
 *
 *   SELECTIVITY    once the metal clears, metal and dielectric polish at
 *                  different rates, and every second past that point buys
 *                  dishing and erosion.
 *
 * WHAT IS DELIBERATELY SIMPLE
 *
 * The die is described as a handful of pattern regions rather than a layout.
 * That is not a shortcut so much as what real CMP models do - density is
 * computed on a grid of tens of microns, because that is the length scale the
 * pad averages over and finer detail does not survive the averaging.
 */

import { clamp } from '../../core/math'

// ---------------------------------------------------------------- constants

/**
 * Preston coefficient for copper under a standard alumina/oxidiser slurry
 * [1/Pa]. Set so the qualified recipe - 20 kPa with both spindles at 60 rpm,
 * which works out to 1.38 m/s - removes about 600 nm/min, which is where a
 * production copper polish actually runs. Published Preston coefficients for
 * CMP sit between 1e-13 and 1e-12, so this is squarely inside the range rather
 * than fitted to the edge of it.
 */
const PRESTON_COPPER = 3.6e-13

/**
 * Metal-to-dielectric removal selectivity of a fresh slurry.
 *
 * The inhibitor in a copper slurry passivates exposed dielectric, and the
 * resulting 25:1 is what turns "polish until it looks right" into a process
 * with a natural stopping point.
 */
const SELECTIVITY_FRESH = 25

/**
 * Pad conformability length [um].
 *
 * How far the pad can press into a recess. Sets which lines dish: far below
 * this the pad bridges the trench and the metal is protected, far above it the
 * pad reaches the bottom and the metal polishes at its own rate. Polishing pads
 * are millimetre-scale stiff and micron-scale soft, and this is where the
 * transition sits.
 */
const PAD_REACH_UM = 15

/**
 * How fast metal that has already recessed below the surface comes off,
 * relative to blanket copper.
 *
 * This was missing from the first version of the model, and its absence made
 * dishing and erosion come out around ten times too large - hundreds of
 * nanometres where a real polish gives tens.
 *
 * The reason it belongs here: blanket copper is being continuously cut open by
 * the abrasive, so the oxidiser always meets fresh metal. Once a line recesses,
 * the pad only reaches it intermittently, the slurry's inhibitor is not being
 * scrubbed off, and the passivating film survives between contacts. Removal
 * drops sharply. It is the same chemistry that gives the process its
 * selectivity, applied to metal instead of dielectric.
 *
 * This is the one calibration in the dishing model. The harness asserts the
 * ORDERING it produces - wider lines dish more, more over-polish dishes more,
 * denser arrays erode more - and never the magnitude.
 */
const RECESSED_METAL_FRACTION = 0.15

/**
 * Fraction of the wafer that has to transition before the endpoint detector
 * calls it.
 *
 * A motor-current trace is a bulk friction measurement. It cannot see the last
 * few per cent of area still carrying metal, and that blind spot is exactly
 * what the over-polish setting is for. Seventy per cent is where a real
 * detector's slope change becomes unambiguous.
 */
const ENDPOINT_CLEARED_FRACTION = 0.7

/** Distance from platen centre to wafer centre [m]. */
const PLATEN_OFFSET_M = 0.22

/** Wafer radius [m]. */
const WAFER_RADIUS_M = 0.15

// ------------------------------------------------------------------ recipe

export interface CmpRecipe {
  /** Down force at the carrier [kPa]. Two to six psi in the trade. */
  downForceKPa: number
  /** Platen rotation [rpm]. */
  platenRpm: number
  /** Carrier rotation [rpm]. */
  carrierRpm: number
  /** Slurry flow [mL/min]. */
  slurryFlowMlMin: number
  /**
   * Extra polish past endpoint, as a percentage of the time it took to reach
   * it. The single most consequential number on the tool: too little leaves
   * metal bridging every line, too much dishes every wide one.
   */
  overpolishPct: number
  /** Copper overburden above the trench tops at the start [nm]. */
  overburdenNm: number
  /** Step height left over the trenches by the plating step [nm]. */
  incomingStepNm: number
}

export const DEFAULT_CMP_RECIPE: CmpRecipe = {
  downForceKPa: 20,
  platenRpm: 60,
  carrierRpm: 60,
  slurryFlowMlMin: 200,
  overpolishPct: 20,
  overburdenNm: 600,
  incomingStepNm: 120,
}

export const CMP_LIMITS: Record<
  keyof CmpRecipe,
  { min: number; max: number; step: number; unit: string; label: string }
> = {
  downForceKPa: { min: 5, max: 55, step: 1, unit: 'kPa', label: 'Down force' },
  platenRpm: { min: 10, max: 150, step: 5, unit: 'rpm', label: 'Platen speed' },
  carrierRpm: { min: 10, max: 150, step: 5, unit: 'rpm', label: 'Carrier speed' },
  slurryFlowMlMin: { min: 50, max: 400, step: 10, unit: 'mL/min', label: 'Slurry flow' },
  overpolishPct: { min: 0, max: 120, step: 5, unit: '%', label: 'Over-polish' },
  overburdenNm: { min: 200, max: 1600, step: 50, unit: 'nm', label: 'Incoming overburden' },
  incomingStepNm: { min: 0, max: 600, step: 20, unit: 'nm', label: 'Incoming step height' },
}

// --------------------------------------------------------------- condition

export interface CmpCondition {
  /**
   * Pad glaze, 0 freshly conditioned to 1 fully glazed. A glazed pad has lost
   * the open pore structure that carries slurry into the contact, so it removes
   * less for the same force and speed.
   */
  padGlaze: number
  /** Retaining ring wear, 0 new to 1 past its limit. A worn ring stops
   *  shielding the wafer edge from the pad's rebound, and edge pressure rises. */
  ringWear: number
  /**
   * Slurry age, 0 fresh to 1 past shelf life. Aged slurry loses the inhibitor
   * that passivates dielectric long before it loses much of its abrasive, so
   * SELECTIVITY collapses while the removal rate barely moves. That asymmetry
   * is what makes it diagnosable at all.
   */
  slurryAge: number
  /**
   * Carrier centre-zone pressure error, as a fraction. A multi-zone carrier
   * trims the radial pressure profile; when a zone regulator drifts, the
   * profile tilts and one part of the wafer polishes faster than the rest.
   */
  centreZoneBias: number
}

export const NOMINAL_CMP_CONDITION: CmpCondition = {
  padGlaze: 0,
  ringWear: 0,
  slurryAge: 0,
  centreZoneBias: 0,
}

// ------------------------------------------------------------------ layout

/**
 * A patch of die with one pattern density, as CMP sees it.
 *
 * Not a layout - a density map on the scale the pad averages over. Real models
 * do exactly this, because the pad spans tens of microns and cannot resolve
 * anything finer.
 */
export interface PatternRegion {
  id: string
  label: string
  /** Fraction of the region that is metal trench, 0 to 1. */
  metalDensity: number
  /** Width of a single line in the region [um]. Sets how far the pad reaches in. */
  lineWidthUm: number
}

/**
 * The test die. Three regions chosen because between them they produce every
 * failure mode this tool has:
 *
 *   a wide bond pad          almost all trench, so very little of it touches
 *                            the pad. What does touch carries the whole load,
 *                            it clears early, and then it dishes for the rest
 *                            of the polish because the pad reaches all the way
 *                            into an 80 um line.
 *   a dense signal array     erodes, because metal and dielectric come down
 *                            together and the array sinks below the field.
 *   an isolated fine line    mostly field, so the load is spread over nearly
 *                            all of it and it clears LAST. It sets the
 *                            endpoint, and everything else on the die is
 *                            over-polishing while it finishes.
 */
export const DEFAULT_LAYOUT: PatternRegion[] = [
  { id: 'pad', label: 'Bond pad, 80 um', metalDensity: 0.85, lineWidthUm: 80 },
  { id: 'array', label: 'Dense array, 0.5 um', metalDensity: 0.55, lineWidthUm: 0.5 },
  { id: 'iso', label: 'Isolated line, 2 um', metalDensity: 0.12, lineWidthUm: 2 },
]

// ------------------------------------------------------------------ results

export interface RegionResult {
  region: PatternRegion
  /** Time the overburden cleared here [s]. Infinity if it never did. */
  clearedAtS: number
  /** Metal still bridging over the dielectric at the end [nm]. Zero is cleared. */
  residueNm: number
  /** Metal recessed below the surrounding dielectric [nm]. */
  dishingNm: number
  /** Dielectric lost from the array relative to the unpatterned field [nm]. */
  erosionNm: number
  /** How far this region's surface sits below the field [nm]. */
  surfaceLossNm: number
}

export interface CmpResult {
  /** Blanket removal rate at the wafer centre [nm/min]. */
  rateNmMin: number
  /** Relative pad speed at the centre and at the edge [m/s]. */
  velocityCentre: number
  velocityEdge: number
  /** Half-range non-uniformity of removal across the wafer [%]. */
  removalNonUniformityPct: number
  /** Radial removal profile [nm]. */
  removalNm: Float64Array
  /** Normalised radius of each sample. */
  x: Float64Array
  /** Time the last part of the wafer cleared [s], and the polish time run. */
  endpointS: number
  totalTimeS: number
  regions: RegionResult[]
  /**
   * Worst-to-best spread of the finished surface across the die [nm].
   *
   * The number the NEXT layer inherits as a focus error, and the reason this
   * tool exists. Everything else here is in service of it.
   */
  surfaceRangeNm: number
  /** True when metal is still bridging somewhere - an electrical short. */
  shorted: boolean
  /** Selectivity actually achieved, after the slurry's condition. */
  selectivity: number
}

// ----------------------------------------------------------------- physics

/**
 * Relative pad speed at a point on the wafer [m/s].
 *
 * The result worth knowing. With the platen at w_p about its own centre and the
 * carrier at w_c about the wafer centre, a point at position r from the wafer
 * centre moves relative to the pad at
 *
 *     v = w_p x d + (w_p - w_c) x r
 *
 * so when the two speeds are equal the second term vanishes and every point on
 * the wafer sees the same speed, exactly. Run them apart and the wafer develops
 * a radial rate signature that no amount of pressure trimming will fix.
 */
export function relativeVelocity(recipe: CmpRecipe, radiusM: number): number {
  const wp = (recipe.platenRpm * 2 * Math.PI) / 60
  const wc = (recipe.carrierRpm * 2 * Math.PI) / 60
  const base = wp * PLATEN_OFFSET_M
  const differential = (wp - wc) * radiusM
  // The two terms are perpendicular only in the worst case; taking them in
  // quadrature is the standard treatment and is exact when they are.
  return Math.sqrt(base * base + differential * differential)
}

/**
 * Down-force profile across the wafer [Pa].
 *
 * Two departures from flat, and they are the two the technician can do
 * something about: the carrier's zone regulators, and the retaining ring. A
 * worn ring stops carrying its share of the load, the pad rebounds at the wafer
 * edge, and the last few millimetres see more pressure than the recipe asked
 * for.
 */
export function pressureAt(recipe: CmpRecipe, condition: CmpCondition, x: number): number {
  const base = recipe.downForceKPa * 1000
  const zone = 1 + condition.centreZoneBias * (1 - x * x)
  const ring = 1 + 0.55 * condition.ringWear * Math.pow(clamp(x, 0, 1), 6)
  return base * zone * ring
}

/** Preston coefficient after the pad and slurry have had their say [1/Pa]. */
/**
 * Preston's equation: removal rate [m/s] from pressure and relative speed.
 *
 * Published for plate glass in 1927 and still the working model for every
 * polishing process in the building. Stated on its own, in SI, so that the
 * two tools that obey it - CMP-11 planarising copper and WPR-08 polishing
 * bare silicon - are running one equation with two coefficients rather than
 * two implementations that happen to agree today.
 *
 *     MRR = Kp · P · v
 *
 * The coefficient is the material pair and the consumable set. Everything
 * else is mechanics.
 */
export const prestonRemovalRate = (kp: number, pressurePa: number, velocityMs: number): number =>
  kp * Math.max(pressurePa, 0) * Math.max(velocityMs, 0)

export function prestonCoefficient(condition: CmpCondition): number {
  // A glazed pad loses most of its removal; aged slurry loses a little.
  const glaze = 1 - 0.72 * clamp(condition.padGlaze, 0, 1)
  const age = 1 - 0.12 * clamp(condition.slurryAge, 0, 1)
  return PRESTON_COPPER * glaze * age
}

/**
 * Selectivity after the slurry has aged.
 *
 * The inhibitor goes first. That is the whole diagnostic signature of old
 * slurry: the tool still removes at very nearly the rated speed, so nothing on
 * the rate chart moves, and every wide line on the wafer dishes.
 */
export function selectivityOf(condition: CmpCondition): number {
  const age = clamp(condition.slurryAge, 0, 1)
  return 1 + (SELECTIVITY_FRESH - 1) * (1 - 0.85 * age)
}

/**
 * How fast recessed metal comes off, after the slurry's condition.
 *
 * The first version held this fixed and the model promptly contradicted itself:
 * ageing the slurry raised the dielectric rate past the recessed-metal rate, so
 * old slurry came out REDUCING dishing. That is backwards, and the reason was a
 * missing consequence rather than a wrong number.
 *
 * The inhibitor does not only passivate dielectric. It is also what keeps a
 * recessed copper line from polishing at the blanket rate, because between pad
 * contacts the film has time to re-form. Lose the inhibitor and both surfaces
 * are attacked - so the dielectric rate rises AND the metal stops being
 * protected, and the wide lines dish for it.
 */
export function recessedMetalFraction(condition: CmpCondition): number {
  const age = clamp(condition.slurryAge, 0, 1)
  return RECESSED_METAL_FRACTION + (1 - RECESSED_METAL_FRACTION) * 0.55 * age
}

/** How far the pad reaches into a line of a given width, 0 to 1. */
export const padReach = (widthUm: number): number => widthUm / (widthUm + PAD_REACH_UM)

// -------------------------------------------------------------------- run

const SAMPLES = 32

export function polish(
  recipe: CmpRecipe,
  condition: CmpCondition = NOMINAL_CMP_CONDITION,
  layout: PatternRegion[] = DEFAULT_LAYOUT,
): CmpResult {
  const kp = prestonCoefficient(condition)
  const selectivity = selectivityOf(condition)
  const recessedFraction = recessedMetalFraction(condition)

  const x = new Float64Array(SAMPLES)
  for (let i = 0; i < SAMPLES; i++) x[i] = i / (SAMPLES - 1)

  // Blanket metal removal rate at each radius, from Preston.
  const rate = new Float64Array(SAMPLES)
  for (let i = 0; i < SAMPLES; i++) {
    const r = x[i] * WAFER_RADIUS_M
    rate[i] = kp * pressureAt(recipe, condition, x[i]) * relativeVelocity(recipe, r) // m/s
  }

  const rateAt = (i: number) => rate[i] * 1e9 // nm/s

  /**
   * When a region clears at a given radius.
   *
   * Two phases. While a step remains, only the raised fraction is in contact,
   * so it carries all the load and polishes at the blanket rate divided by that
   * fraction - which is why an isolated feature disappears while a dense array
   * has barely started. Once the step closes, the rest of the overburden comes
   * off at the blanket rate.
   */
  function clearTime(i: number, region: PatternRegion): number {
    const up = clamp(1 - region.metalDensity, 0.02, 1)
    const r = rateAt(i)
    if (r <= 0) return Infinity
    // Phase one: only the raised fraction touches the pad, so it carries the
    // whole load and comes down at the blanket rate divided by that fraction.
    // The step closes when the raised area has lost its own height.
    const stepPhase = (recipe.incomingStepNm * up) / r
    // Phase two: the surface is flat, everything is in contact, and the rest of
    // the overburden comes off at the blanket rate. `overburdenNm` is measured
    // from the trench tops on the flat, so it is not reduced by phase one.
    return stepPhase + recipe.overburdenNm / r
  }

  // Endpoint fires when the BULK of the wafer transitions, not when the last
  // stubborn spot does.
  //
  // This is the whole reason over-polish exists, and getting it wrong made the
  // model incoherent: an endpoint defined as "everything has cleared" means
  // stopping at endpoint always clears everything, and no setting could ever
  // leave a residue. A real detector watches motor current, and the friction
  // only changes once most of the area has gone from metal to dielectric. The
  // tail clears afterwards, on time the recipe has to pay for deliberately.
  const cleared: Array<{ t: number; weight: number }> = []
  let totalWeight = 0
  for (let i = 0; i < SAMPLES; i++) {
    // Annulus area, so the outer wafer counts for what it actually is.
    const w = Math.max(x[i], 0.5 / SAMPLES)
    for (const region of layout) {
      const t = clearTime(i, region)
      if (Number.isFinite(t)) {
        cleared.push({ t, weight: w })
        totalWeight += w
      }
    }
  }
  cleared.sort((a, b) => a.t - b.t)
  let endpointS = 0
  let accumulated = 0
  for (const c of cleared) {
    accumulated += c.weight
    endpointS = c.t
    if (accumulated >= ENDPOINT_CLEARED_FRACTION * totalWeight) break
  }
  const totalTimeS = endpointS * (1 + recipe.overpolishPct / 100)

  // Total metal removed at each radius over the whole polish.
  const removalNm = new Float64Array(SAMPLES)
  for (let i = 0; i < SAMPLES; i++) removalNm[i] = rateAt(i) * totalTimeS

  // Region outcomes, evaluated across the whole wafer rather than at one
  // radius. That coupling matters: a residue anywhere on the wafer is a short,
  // and the place that polishes fastest is the place that dishes worst, so a
  // non-uniform tool is punished at both ends at once.
  const regions: RegionResult[] = layout.map((region) => {
    let slowest = 0
    let fastest = Infinity
    for (let i = 0; i < SAMPLES; i++) {
      const t = clearTime(i, region)
      if (t > slowest) slowest = t
      if (t < fastest) fastest = t
    }
    // Clearing is reported where it happens LAST, because that is when the
    // region is actually done. Dishing is reported where the region cleared
    // FIRST, because that spot has been over-polishing the longest.
    const clearedAtS = slowest
    const cleared = slowest <= totalTimeS
    const over = Math.max(totalTimeS - fastest, 0)
    const rBlanket = rateAt(SAMPLES - 1)
    const rDielectric = rBlanket / selectivity
    // Once the line is recessed it is no longer blanket copper - see
    // RECESSED_METAL_FRACTION. Everything past the clear point runs at this
    // rate, which is why a real polish dishes tens of nanometres rather than
    // the hundreds a blanket rate would predict.
    const rMetal = rBlanket * recessedFraction

    // After clearing, the array as a whole recedes at the density-weighted
    // rate while the unpatterned field only sees the dielectric rate. The
    // difference IS erosion - it is derived, not fitted.
    const arrayRate = region.metalDensity * rMetal + (1 - region.metalDensity) * rDielectric
    const erosionNm = cleared ? (arrayRate - rDielectric) * over : 0

    // Within the array, metal sits below its neighbouring dielectric by as
    // much as the pad can reach into it.
    const dishingNm = cleared
      ? Math.max(0, padReach(region.lineWidthUm) * (rMetal - rDielectric) * over)
      : 0

    return {
      region,
      clearedAtS,
      residueNm: cleared ? 0 : (slowest - totalTimeS) * rBlanket,
      dishingNm,
      erosionNm,
      surfaceLossNm: erosionNm + dishingNm,
    }
  })

  // The number litho inherits: the worst-to-best spread of the finished
  // surface, including anything that never cleared and is therefore standing
  // proud of everything around it.
  const losses = regions.map((r) => r.surfaceLossNm)
  const proud = regions.map((r) => r.residueNm)
  const surfaceRangeNm = Math.max(...losses, ...proud) - Math.min(...losses, 0)

  const mean = removalNm.reduce((a, b) => a + b, 0) / SAMPLES
  const lo = Math.min(...removalNm)
  const hi = Math.max(...removalNm)

  return {
    rateNmMin: rateAt(0) * 60,
    velocityCentre: relativeVelocity(recipe, 0),
    velocityEdge: relativeVelocity(recipe, WAFER_RADIUS_M),
    removalNonUniformityPct: mean > 0 ? (100 * (hi - lo)) / (2 * mean) : 0,
    removalNm,
    x,
    endpointS,
    totalTimeS,
    regions,
    surfaceRangeNm,
    shorted: regions.some((r) => r.residueNm > 0),
    selectivity,
  }
}

export const CMP_PRESETS: Record<
  string,
  { label: string; note: string; recipe: CmpRecipe }
> = {
  baseline: {
    label: 'BASELINE-CU-DAMASCENE',
    note: 'Qualified copper polish: matched platen and carrier speed, 20% over-polish.',
    recipe: { ...DEFAULT_CMP_RECIPE },
  },
  underPolished: {
    label: 'NO OVER-POLISH',
    note:
      'Stops the instant the endpoint trace moves. On a perfectly uniform tool that is just ' +
      'about survivable; on any real one, wherever the wafer polished slow still has metal ' +
      'bridging the lines, which is a short, which is a dead die.',
    recipe: { ...DEFAULT_CMP_RECIPE, overpolishPct: 0 },
  },
  overPolished: {
    label: 'HEAVY OVER-POLISH',
    note: 'Double the over-polish to be safe about clearing. Every wide line dishes for it.',
    recipe: { ...DEFAULT_CMP_RECIPE, overpolishPct: 100 },
  },
  mismatchedSpeeds: {
    label: 'SPEEDS MISMATCHED',
    note:
      'Carrier at half the platen speed. Relative velocity stops being uniform across the wafer ' +
      'and a radial signature appears that pressure trimming cannot remove.',
    recipe: { ...DEFAULT_CMP_RECIPE, carrierRpm: 30 },
  },
  hardPress: {
    label: 'HIGH DOWN FORCE',
    note: 'Force up to 40 kPa for throughput. Faster, and every defect mechanism scales with it.',
    recipe: { ...DEFAULT_CMP_RECIPE, downForceKPa: 40 },
  },
}
